import { configAtom } from "@/config.ts";
import { addDevVars } from "@/dev/initDev.ts";
import { makeClictopayLink } from "@/modules/billing/clictopay/makeClictopayLink.tsx";
import { makeCoingateLink } from "@/modules/billing/coingate/makeCoingateLink.tsx";
import { makeDuitkuLink } from "@/modules/billing/duitku/makeDuitku.tsx";
import { makeEvcHormuudLink } from "@/modules/billing/evc_hormuud/makeEvcHormuudLink.tsx";
import { makeOmiseCard, makeOmiseLink } from "@/modules/billing/omise/makeOmise.tsx";
import { defaults } from "@/modules/billing/paymentMethodDefaults.tsx";
import type { CardMethod, InvoiceMethod, PaymentMethod } from "@/modules/billing/paymentMethods.ts";
import { makePaypalVault } from "@/modules/billing/paypal/makePaypal.tsx";
import { makePaypalCheckout } from "@/modules/billing/paypal/makePaypalLink.tsx";
import { makeRazorpayLink } from "@/modules/billing/razorpay/makeRazorpayLink.tsx";
import { makeSslcommerzLink } from "@/modules/billing/sslcommerz/makeSslcommerz.tsx";
import { makeStripe } from "@/modules/billing/stripe/makeStripe.tsx";
import { makeStripeLink } from "@/modules/billing/stripe/makeStripeLink.tsx";
import { showWarn } from "@/modules/error/errorStream.ts";
import type { InvoiceConfig, ProcessorConfig } from "@warrenio/api-spec/spec.oats.gen";
import { mapFrom } from "@warrenio/utils/collections/maps";
import { unreachable } from "@warrenio/utils/unreachable";
import { atom } from "jotai/vanilla";
import React from "react";

function makeInvoice(config: InvoiceConfig): InvoiceMethod {
    return {
        ...defaults(config),
        type: "invoice",

        isAutomated: true,
        canAddToAccount: ({ account, user }) => {
            const isPrePay = account?.isPrePay;
            return !isPrePay
                ? (user?.allow_invoice ?? config.default_allow_invoice ?? true) // additional checks for post-pay accounts
                : false;
        },
        AddForm: React.lazy(() => import("@/modules/billing/choose_method/InvoiceForm.tsx")),
    };
}

function warnUnknownProcessor(config: ProcessorConfig) {
    showWarn(`Unknown payment processor: ${config.type}/${config.keyword}`);
    return undefined;
}

function makeMethod(config: ProcessorConfig): PaymentMethod | PaymentMethod[] | undefined {
    const { type, keyword } = config;
    switch (type) {
        case "creditcard":
            switch (keyword) {
                case "stripe_creditcard":
                    return makeStripe(config);

                case "omise_creditcard":
                    return makeOmiseCard(config);

                case "paypal_vault":
                    return makePaypalVault(config);

                default:
                    return warnUnknownProcessor(config);
            }

        case "invoice":
            return makeInvoice(config);

        case "link":
            switch (keyword) {
                case "duitku":
                    return makeDuitkuLink(config);
                case "sslcommerz":
                    return makeSslcommerzLink(config);
                case "stripe_wallet":
                    return makeStripeLink(config);

                case "razorpay":
                    return makeRazorpayLink(config);
                case "clictopay":
                    return makeClictopayLink(config);
                case "paypal_checkout":
                    return makePaypalCheckout(config);
                case "coingate":
                    return makeCoingateLink(config);
                case "evc_hormuud":
                    return makeEvcHormuudLink(config);

                case "omise":
                    return makeOmiseLink(config);

                default:
                    return warnUnknownProcessor(config);
            }

        case "hybrid":
            return unreachable("Hybrid processor is deprecated");

        default:
            return warnUnknownProcessor(config);
    }
}

function buildMethodsList(configs: ProcessorConfig[]) {
    return configs.flatMap((config) => {
        const methods = makeMethod(config);
        return methods ? (Array.isArray(methods) ? methods : [methods]) : [];
    });
}

export const processorsAtom = atom((get) => mapFrom(buildMethodsList(get(configAtom).paymentProcessors), (p) => p.id));

/** Methods that can be added to a billing account (ie. not historical or temporarily disabled) */
export const addableMethodsAtom = atom((get) => [...get(processorsAtom).values()].filter((p) => p.canAdd));

/** NB: Do not use except for checking if a card exists and backward compatibility */
export const legacy_creditCardProcessorAtom = atom((get) =>
    [...get(processorsAtom).values()].find((p): p is CardMethod => p.type === "creditcard"),
);

export const hasCreditCardProcessorAtom = atom((get) => !!get(legacy_creditCardProcessorAtom));

addDevVars({ processorsAtom });

export function hasProcessorWithAutoTopup(processors: ProcessorConfig[]) {
    // XXX: This duplicates `.isAutomated` logic
    return processors.some((p) => p.type === "creditcard" && p.supports_auto_topup !== false);
}
